import dayjs from 'dayjs'
import stringFields from '../constants/widgets/stringFields'
import dateFields from '../constants/widgets/dateFields'
import dateTimeFields from '../constants/widgets/dateTimeFields'

export const OPERATORS = {
    CONTAINS: { key: 'CONTAINS', label: 'CONTAINS' },
    EQUALS: { key: 'EQUALS', label: '=' },
    GT: { key: 'GT', label: '>' },
    GTE: { key: 'GTE', label: '>=' },
    LT: { key: 'LT', label: '<' },
    LTE: { key: 'LTE', label: '<=' },
    BETWEEN: { key: 'BETWEEN', label: 'BETWEEN' },
    BLANK: { key: 'BLANK', label: 'BLANK' },
}

const STRING_OPERATORS = [OPERATORS.CONTAINS, OPERATORS.EQUALS, OPERATORS.BLANK]
const NUMERIC_OPERATORS = [
    OPERATORS.CONTAINS,
    OPERATORS.EQUALS,
    OPERATORS.GT,
    OPERATORS.GTE,
    OPERATORS.LT,
    OPERATORS.LTE,
    OPERATORS.BETWEEN,
    OPERATORS.BLANK,
]
const DATE_OPERATORS = [
    OPERATORS.CONTAINS,
    OPERATORS.EQUALS,
    OPERATORS.GT,
    OPERATORS.GTE,
    OPERATORS.LT,
    OPERATORS.LTE,
    OPERATORS.BETWEEN,
    OPERATORS.BLANK,
]
const BIT_OPERATORS = [OPERATORS.CONTAINS, OPERATORS.EQUALS, OPERATORS.BLANK]

export const getOperatorsForType = (fieldType) => {
    switch (fieldType) {
        case 'number':
            return NUMERIC_OPERATORS
        case 'date':
            return DATE_OPERATORS
        case 'bit':
            return BIT_OPERATORS
        default:
            return STRING_OPERATORS
    }
}

export const getDefaultOperatorForType = (fieldType) => {
    switch (fieldType) {
        case 'number':
        case 'bit':
            return 'EQUALS'
        default:
            return 'CONTAINS'
    }
}

const isEmptyCell = (val) => {
    if (val === null || val === undefined) return true
    if (typeof val === 'string' && val.trim() === '') return true
    return false
}

const isNumericCell = (val) => {
    if (typeof val === 'boolean') return false
    return !isNaN(val)
}

export const getColumnFieldType = (colName, data) => {
    if (dateFields.includes(colName) || dateTimeFields.includes(colName)) {
        return 'date'
    }
    if (stringFields.includes(colName)) {
        return 'string'
    }

    if (!Array.isArray(data) || data.length === 0) {
        return 'string'
    }

    let sawNumeric = false
    let sawBoolean = false
    for (let i = 0; i < data.length; i++) {
        const row = data[i]
        if (!row) continue
        const val = row[colName]
        if (isEmptyCell(val)) continue
        if (typeof val === 'boolean') {
            sawBoolean = true
            continue
        }
        if (!isNumericCell(val)) {
            return 'string'
        }
        sawNumeric = true
    }

    if (sawBoolean && !sawNumeric) {
        return 'bit'
    }

    return sawNumeric ? 'number' : 'string'
}

const toComparable = (val, fieldType) => {
    if (fieldType === 'number' || (!fieldType && !isNaN(val) && val !== '' && val !== null)) {
        const num = parseFloat(val)
        if (!isNaN(num)) return num
    }
    if (fieldType === 'date' || (!fieldType && dayjs(val).isValid())) {
        const d = dayjs(val)
        if (d.isValid()) return d.valueOf()
    }
    return String(val).toLowerCase()
}

const normalizeCellValue = (val) =>
    typeof val === 'boolean' ? (val ? 1 : 0) : val

export const operatorFilterFunction = (rows, columnIds, filterValue) => {
    if (!filterValue) return rows

    if (typeof filterValue === 'string') {
        const search = filterValue.toLowerCase()
        return rows.filter((row) => {
            const val = row.values[columnIds[0]]
            return val != null && String(val).toLowerCase().includes(search)
        })
    }

    const { operator, value, value2, fieldType } = filterValue
    const colId = columnIds[0]

    if (operator === 'BLANK') {
        return rows.filter((row) => {
            const cellValue = row.values[colId]
            return cellValue == null || String(cellValue).trim() === ''
        })
    }

    if (!value && value !== 0) return rows

    return rows.filter((row) => {
        const cellValue = normalizeCellValue(row.values[colId])
        if (cellValue == null || cellValue === '') return false

        switch (operator) {
            case 'CONTAINS': {
                return String(cellValue)
                    .toLowerCase()
                    .includes(String(value).toLowerCase())
            }
            case 'EQUALS': {
                if (fieldType === 'date') {
                    const cv = dayjs(cellValue)
                    const fv = dayjs(value)
                    return cv.isValid() && fv.isValid() && cv.isSame(fv, 'day')
                }
                if (fieldType === 'number') {
                    return parseFloat(cellValue) === parseFloat(value)
                }
                return String(cellValue).toLowerCase() === String(value).toLowerCase()
            }
            case 'GT': {
                return toComparable(cellValue, fieldType) > toComparable(value, fieldType)
            }
            case 'GTE': {
                return toComparable(cellValue, fieldType) >= toComparable(value, fieldType)
            }
            case 'LT': {
                return toComparable(cellValue, fieldType) < toComparable(value, fieldType)
            }
            case 'LTE': {
                return toComparable(cellValue, fieldType) <= toComparable(value, fieldType)
            }
            case 'BETWEEN': {
                if (!value2 && value2 !== 0) return true
                const cv = toComparable(cellValue, fieldType)
                const v1 = toComparable(value, fieldType)
                const v2 = toComparable(value2, fieldType)
                return cv >= v1 && cv <= v2
            }
            default:
                return true
        }
    })
}
